import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

interface CopyButtonProps {
  /** The text placed on the clipboard when the button is pressed. */
  value: string;
  /** Accessible label describing what is being copied. */
  label?: string;
  className?: string;
}

/**
 * Small clipboard button that flashes a check icon for ~1.5s after a copy.
 * Reused across the connection kit's copyable values.
 */
export function CopyButton({ value, label = "Copy", className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      logger.warn("[copy] clipboard write failed", err);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={() => void handleCopy()}
      aria-label={copied ? "Copied" : label}
      className={cn("h-8 w-8 shrink-0", className)}
    >
      {copied ? (
        <Check className="text-jj-green-03 h-4 w-4" />
      ) : (
        <Copy className="text-muted h-4 w-4" />
      )}
    </Button>
  );
}
